export const CHECKOUT_URL =
  'https://automotive-diagnostic-pro.impultienda.ar/checkout'

export const BOX_IMAGE =
  'https://cdn.magicpatterns.com/uploads/pqoPTwbBGHzZE3oJAQvZxo/Diseno_sin_titulo-3.webp'

export const INSIDE_IMAGES = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/ra72ZiT1wKiXJPKknAXc2w/Captura_de_pantalla_2026-09-14_a_la(s)_8.50.35_p._m..webp',
    alt: 'Short-term & long-term fuel trim diagnostic reference page',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/u3KEcbh3gCYpFHcVecf9KU/Captura_de_pantalla_2026-09-14_a_la(s)_8.54.47_p._m..webp',
    alt: 'MAF data interpretation reference page',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/uCe5k77HY8X6MfC4wopZuD/Captura_de_pantalla_2026-09-14_a_la(s)_8.55.25_p._m..webp',
    alt: 'Throttle and pedal correlation reference page',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/gaoC4toZPbmmT9GMAs3zSc/Captura_de_pantalla_2026-09-14_a_la(s)_8.55.59_p._m..webp',
    alt: 'Decision tree for a component that does not respond to a command',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/5q1yUNjJLCGLPqwSParfEd/Captura_de_pantalla_2026-09-14_a_la(s)_8.56.48_p._m..webp',
    alt: 'Case study: cylinder misfire diagnostic sequence',
  },
]

export interface Module {
  num: number
  title: string
  body: string
  kicker: string
}

export const modules: Module[] = [
  {
    num: 1,
    title: 'AUTOMOTIVE DIAGNOSTIC WORKFLOW SYSTEM™',
    body: 'Follow a structured diagnostic process from customer complaint to root cause. Use symptom verification, preliminary inspection, scan strategy and testing sequences to determine what to check next.',
    kicker: 'Verify. Test. Isolate.',
  },
  {
    num: 2,
    title: 'ENGINE & DRIVEABILITY DIAGNOSTICS™',
    body: 'Troubleshoot no-starts, misfires, rough idle, stalling, hesitation and performance problems. Work through ignition, fuel, airflow, sensor and engine-related diagnostic paths.',
    kicker: 'Observe. Compare. Diagnose.',
  },
  {
    num: 3,
    title: 'AUTOMOTIVE ELECTRICAL DIAGNOSTICS™',
    body: 'Find power, ground, circuit and component faults with structured electrical tests. Work through batteries, starting, charging, wiring, relays, sensors and parasitic-draw problems.',
    kicker: 'Measure. Isolate. Verify.',
  },
  {
    num: 4,
    title: 'OBD-II & SCAN TOOL DIAGNOSTICS™',
    body: 'Turn trouble codes, freeze-frame information and live data into clearer diagnostic decisions. Use DTC workflows, PID comparisons, fuel-trim analysis and scan-tool testing strategies.',
    kicker: 'Scan. Interpret. Test.',
  },
  {
    num: 5,
    title: 'SYMPTOM-BASED FAULT FINDER™',
    body: 'Start directly from the vehicle symptom. Follow practical diagnostic paths for no-starts, misfires, battery drain, warning lights, overheating and other common complaints.',
    kicker: 'Find. Follow. Diagnose.',
  },
  {
    num: 6,
    title: 'ADVANCED ELECTRONIC & NETWORK DIAGNOSTICS™',
    body: 'Troubleshoot communication, module, sensor and vehicle-network problems. Work through U-codes, no-communication faults, module power and ground, CAN network and electronic system diagnostics.',
    kicker: 'Communicate. Isolate. Confirm.',
  },
]

export interface Bonus {
  num: number
  title: string
  subtitle: string
  body: string
  kicker: string
}

export const bonuses: Bonus[] = [
  {
    num: 1,
    title: 'DIAGNOSTIC INTAKE & FIRST-CHECK SYSTEM™',
    subtitle: 'A Ready-to-Use Diagnostic Starting System',
    body: 'Turn customer complaints into a structured diagnostic starting point with symptom capture, condition tracking, preliminary inspections and initial diagnostic planning.',
    kicker: 'Capture. Verify. Start.',
  },
  {
    num: 2,
    title: 'ELECTRICAL TEST DECISION CARDS™',
    subtitle: 'Professional Electrical Testing Reference Cards',
    body: 'Choose the appropriate electrical test for power, ground, voltage drop, open circuits, shorts, relays, sensors and intermittent electrical faults.',
    kicker: 'Choose. Measure. Interpret.',
  },
  {
    num: 3,
    title: 'SCAN DATA INTERPRETATION WORKBOOK™',
    subtitle: 'A Practical Live-Data Diagnostic Workbook',
    body: 'Organize live data, compare related PIDs, analyze fuel trims, evaluate sensor relationships and turn scanner information into testable diagnostic evidence.',
    kicker: 'Read. Compare. Test.',
  },
  {
    num: 4,
    title: 'INTERMITTENT FAULT TRACKING SYSTEM™',
    subtitle: 'A Professional Intermittent Diagnostic System',
    body: 'Capture the conditions behind faults that come and go. Track temperature, vibration, moisture, electrical load and operating conditions to isolate intermittent problems.',
    kicker: 'Track. Reproduce. Isolate.',
  },
  {
    num: 5,
    title: 'POST-REPAIR VERIFICATION & DIAGNOSTIC RECORD SYSTEM™',
    subtitle: 'Ready-to-Use Verification & Documentation Tools',
    body: 'Document diagnostic evidence, compare before-and-after results and verify that the original symptom has been corrected after the repair.',
    kicker: 'Repair. Verify. Document.',
  },
]

export interface Testimonial {
  name: string
  role: string
  location: string
  quote: string
}

export const testimonials: Testimonial[] = [
  {
    name: 'Darren Whitfield, 41',
    role: 'Master technician, independent workshop',
    location: 'Manchester, United Kingdom',
    quote:
      'I have been turning spanners for nineteen years and electrical faults were still the jobs I dreaded quoting. The electrical test cards changed how I start: power, ground, voltage drop, in that order, every time. Last week a no-crank came in and I had it isolated to a corroded ground strap in under twenty minutes instead of chasing the starter.',
  },
  {
    name: 'Miguel Santoro, 34',
    role: 'Diagnostic technician, dealership service lane',
    location: 'Phoenix, Arizona, United States',
    quote:
      'The fuel-trim and PID comparison pages are the part I actually use daily. I used to look at a lean code and start guessing between the MAF, a vacuum leak and the injectors. Now I follow the comparison sequence and the data narrows it down before I pick up a tool. My comeback rate has dropped noticeably this quarter.',
  },
  {
    name: 'Ryan Kovacs, 27',
    role: 'Third-year apprentice mechanic',
    location: 'Calgary, Alberta, Canada',
    quote:
      'As an apprentice the hardest part was not knowing what to test first, and I did not want to keep asking the senior tech. The symptom-based fault finder gives me a path I can follow on my own and then explain. I still check the manufacturer procedure, but I walk up to the car with a plan now instead of a hunch.',
  },
  {
    name: 'Peter Callaghan, 52',
    role: 'Workshop owner, two-bay garage',
    location: 'Brisbane, Queensland, Australia',
    quote:
      'What sold me was the intermittent fault tracking system. Faults that come and go used to eat entire afternoons and I would hand the car back with nothing confirmed. Logging temperature, load and conditions gave me something real to work from, and the post-repair verification sheets have ended arguments with customers about whether the fault was fixed.',
  },
  {
    name: 'Stephen Adeyemi, 46',
    role: 'Automotive instructor and mobile diagnostic tech',
    location: 'Birmingham, United Kingdom',
    quote:
      'I print the decision trees for my students and use the same pages on mobile call-outs. The network diagnostics module is the clearest treatment of U-codes and no-communication faults I have handed to a class. It is a reference system, not theory, and that is exactly why the lads actually open it.',
  },
]

export interface Faq {
  q: string
  a: string
}

export const faqs: Faq[] = [
  {
    q: 'Who is this system for?',
    a: 'Automotive technicians, mechanics, diagnostic technicians, apprentices and automotive instructors.',
  },
  {
    q: 'Is this a physical product?',
    a: 'No. AUTOMOTIVE DIAGNOSTIC PRO™ is a 100% digital resource.',
  },
  {
    q: 'Is this a training course or certification?',
    a: 'No. It is a practical professional automotive diagnostic reference system.',
  },
  {
    q: 'Is it another theory-heavy ebook?',
    a: 'No. It focuses on visual diagnostic workflows, testing guides, decision trees, scan-data references and workshop tools.',
  },
  {
    q: 'What areas are included?',
    a: 'Diagnostic workflow, engine and driveability, electrical systems, OBD-II, symptom-based diagnosis and electronic networks.',
  },
  {
    q: 'Does this replace professional procedures or manufacturer instructions?',
    a: 'No. Always follow manufacturer documentation, professional safety procedures and applicable workplace requirements.',
  },
  {
    q: 'How will I receive it?',
    a: "You'll receive immediate digital access after purchase.",
  },
  {
    q: 'Do I need a specific scan tool or brand of equipment?',
    a: 'No. The workflows, testing sequences and scan-data references are written to be used alongside the equipment you already work with.',
  },
  {
    q: 'Can I use it on a phone or tablet in the workshop?',
    a: 'Yes. The material is digital and can be opened on a phone, tablet or workshop computer, or printed for the bay.',
  },
  {
    q: 'What if it is not right for me?',
    a: 'You have 7 days. Contact us within 7 days for a full refund according to the refund terms.',
  },
]

export interface Buyer {
  name: string
  city: string
  country: string
}

export const buyers: Buyer[] = [
  { name: 'James T.', city: 'Manchester', country: 'United Kingdom' },
  { name: 'Michael R.', city: 'Houston, TX', country: 'United States' },
  { name: 'Liam W.', city: 'Toronto, ON', country: 'Canada' },
  { name: 'Daniel K.', city: 'Melbourne, VIC', country: 'Australia' },
  { name: 'Robert S.', city: 'Birmingham', country: 'United Kingdom' },
  { name: 'Chris M.', city: 'Phoenix, AZ', country: 'United States' },
  { name: 'Ethan B.', city: 'Calgary, AB', country: 'Canada' },
  { name: 'Jack P.', city: 'Brisbane, QLD', country: 'Australia' },
  { name: 'Andrew L.', city: 'Glasgow', country: 'United Kingdom' },
  { name: 'Tyler D.', city: 'Denver, CO', country: 'United States' },
  { name: 'Noah H.', city: 'Vancouver, BC', country: 'Canada' },
  { name: 'Mason G.', city: 'Perth, WA', country: 'Australia' },
]
